import { Button, Link, LoaderScreen } from '@/components/Elements';
import { AlertCaution } from '@/features/analyze/components/Filter/SavedFilter/AlertCaution';
import { Pagination } from '@/features/analyze/components/Pagination';
import { ResultNotFond } from '@/features/analyze/components/ResultNotFond';
import { SearchResult } from '@/features/analyze/components/SearchResult';
import { graphql, useQuery } from '@/lib/graphql';
import { format } from '@formkit/tempo';
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getCurrentLanguage } from '@/lib/i18n/getCurrentLanguage';

const GET_ALERT_RUN_ARTICLES_QUERY = graphql(
  `
    query getAlertRunArticles($alertRunId: ID!, $page: Int!, $perPage: Int!) {
      articlesByAlertRun(
        alertRunId: $alertRunId
        page: $page
        perPage: $perPage
      ) {
        alertRun {
          savedFilterName
          executionDate
        }
        items {
          ...SearchResultItemFragment
        }
        pageInfo {
          currentPage
          perPage
          totalCount
          hasNextPage
          hasPreviousPage
          lastPage
        }
      }
    }
  `,
  [SearchResult.fragments.data]
);

export const SavedFilterAlertArticles = () => {
  const { t } = useTranslation();
  const { alertRunId } = useParams<{ alertRunId: string }>();
  const [showCaution, setShowCaution] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;

  const {
    data,
    loading: isLoading,
    error,
  } = useQuery(GET_ALERT_RUN_ARTICLES_QUERY, {
    variables: {
      alertRunId: alertRunId || '',
      page: currentPage,
      perPage,
    },
    skip: !alertRunId,
  });

  if (isLoading) return <LoaderScreen />;

  if (error) {
    return (
      <div className="mt-8 px-5 text-center text-red-600 xl:px-36">
        {t('エラーが発生しました')}: {error.message}
      </div>
    );
  }

  const docs = data?.articlesByAlertRun?.items;
  const alertRun = data?.articlesByAlertRun?.alertRun;
  const pageInfo = data?.articlesByAlertRun?.pageInfo;

  if (!docs?.length) return <ResultNotFond />;

  return (
    <div className="px-5 py-10 xl:px-48">
      <div className="mb-6 w-full">
        <Link to="/themes" className="mb-4 flex items-center gap-2 text-sm">
          <ArrowLeftIcon className="size-4" /> {t('トップに戻る')}
        </Link>

        <div className="border-b border-gray-200 pb-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <h1 className="break-words text-xl font-semibold text-gray-900 sm:text-2xl">
              {t('{{date}}実行の「{{filterName}}」の新着記事', {
                date: alertRun?.executionDate
                  ? format(
                      new Date(alertRun.executionDate),
                      'long',
                      getCurrentLanguage()
                    )
                  : '',
                filterName: alertRun?.savedFilterName,
              })}
            </h1>

            <Button
              color="transparent"
              size="xs"
              startIcon={<InformationCircleIcon className="size-4" />}
              endIcon={
                showCaution ? (
                  <ChevronUpIcon className="size-3" />
                ) : (
                  <ChevronDownIcon className="size-3" />
                )
              }
              onClick={() => setShowCaution(!showCaution)}
            >
              {t('通知について')}
            </Button>
          </div>
        </div>

        {showCaution && (
          <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <AlertCaution hideOverwriteSettingNotice />
          </div>
        )}
      </div>

      <SearchResult docs={docs} />

      {pageInfo && (
        <div className="mt-8 flex justify-center">
          <Pagination
            pageInfo={pageInfo}
            onClick={(pageStart) =>
              setCurrentPage(Math.floor(pageStart / perPage) + 1)
            }
          />
        </div>
      )}
    </div>
  );
};
